import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import { IoMdArrowBack } from "react-icons/io";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { hrRecordsApi } from "../../Services/hrRecordsApi";
import { HiOutlinePlus } from "react-icons/hi";
import {
  FITNESS_STATUS_OPTIONS,
  buildEmployeeSelectOptions,
  buildSimpleOptions,
  downloadBlobResponse,
  getApiErrorMessage,
  mergeEmployeeSelectOptions,
  openBlobResponse,
} from "../hrRecordsHelpers";
import HrUploadControl from "../HrUploadControl";
import "../../forms/UserForm.css";
import "../hrRecords.css";

const parseApiDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const toApiDate = (date) => {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const emptyEmployeeRow = () => ({
  actualManPowerId: "",
  reportNumber: "",
  fitnessStatus: "",
  remarks: "",
  files: [],
});

const normaliseFileList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (value.file_id || value.filename || value.name) return [value];
  return Object.values(value).flatMap((item) => normaliseFileList(item));
};

function MedicalReportFormPage({ isSidebarExpanded, mode = "create" }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = mode === "edit";
  const [formData, setFormData] = useState({
    medicalType: "",
    examinationDate: null,
    validFrom: null,
    validTo: null,
    medicalCenter: "",
    examiningDoctor: "",
    fitnessStatus: "",
    remarks: "",
  });
  const [employeeRows, setEmployeeRows] = useState([emptyEmployeeRow()]);
  const [employees, setEmployees] = useState([]);
  const [medicalTypes, setMedicalTypes] = useState([]);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(isEdit);
  const [errors, setErrors] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const fetchLookups = async () => {
    const [employeeResponse, typeResponse] = await Promise.all([
      hrRecordsApi.fetchEmployeesDropdown(),
      hrRecordsApi.getMedicalTypes(),
    ]);

    setEmployees(buildEmployeeSelectOptions(employeeResponse.data || []));
    setMedicalTypes(buildSimpleOptions(typeResponse || [], "type_name", "type_name"));
  };

  const fetchReport = async () => {
    if (!isEdit || !id) return;
    const report = await hrRecordsApi.getMedicalReport(id);

    setFormData({
      medicalType: report.medical_type || "",
      examinationDate: parseApiDate(report.examination_date),
      validFrom: parseApiDate(report.valid_from),
      validTo: parseApiDate(report.valid_to),
      medicalCenter: report.medical_center || "",
      examiningDoctor: report.examining_doctor || "",
      fitnessStatus: report.fitness_status || "",
      remarks: report.remarks || "",
    });
    setEmployeeRows([
      {
        actualManPowerId: String(report.actual_man_power_id || ""),
        reportNumber: report.report_number || "",
        fitnessStatus: report.fitness_status || "",
        remarks: report.remarks || "",
        files: [],
      },
    ]);
    setEmployees((currentEmployees) =>
      mergeEmployeeSelectOptions(currentEmployees, [report]),
    );
    setExistingAttachments(
      normaliseFileList(
        report.attachments ||
          report.files ||
          report.file_details ||
          report.report_files ||
          report.grouped_attachments,
      ),
    );
  };

  useEffect(() => {
    const run = async () => {
      try {
        setPageLoading(true);
        await fetchLookups();
        await fetchReport();
      } catch (error) {
        console.error("Failed to load medical report form:", error);
        showSnackbar("Failed to load medical report data", "error");
      } finally {
        setPageLoading(false);
      }
    };
    run();
  }, [id, isEdit]);

  const selectedEmployeeValues = employeeRows
    .map((row) => row.actualManPowerId)
    .filter(Boolean);

  const handleRowChange = (index, field, value) => {
    setEmployeeRows((prev) =>
      prev.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: value } : row,
      ),
    );
  };

  const addRow = () => {
    setEmployeeRows((prev) => [...prev, emptyEmployeeRow()]);
  };

  const removeRow = (index) => {
    setEmployeeRows((prev) => {
      const next = prev.filter((_, rowIndex) => rowIndex !== index);
      return next.length ? next : [emptyEmployeeRow()];
    });
  };

  const validate = () => {
    const nextErrors = {};
    if (!formData.medicalType) nextErrors.medicalType = "Medical type is required";
    if (!formData.examinationDate)
      nextErrors.examinationDate = "Examination date is required";
    const validRows = employeeRows.filter((row) => row.actualManPowerId);
    if (!validRows.length) nextErrors.employeeRows = "Select at least one employee";
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const basePayload = {
    medical_type: formData.medicalType,
    examination_date: toApiDate(formData.examinationDate),
    valid_from: toApiDate(formData.validFrom) || undefined,
    valid_to: toApiDate(formData.validTo) || undefined,
    medical_center: formData.medicalCenter || undefined,
    examining_doctor: formData.examiningDoctor || undefined,
    fitness_status: formData.fitnessStatus || undefined,
    remarks: formData.remarks || undefined,
  };

  const saveCreate = async () => {
    const validRows = employeeRows.filter((row) => row.actualManPowerId);
    const response = await hrRecordsApi.createMedicalReport({
      ...basePayload,
      employees: validRows.map((row) => ({
        actual_man_power_id: row.actualManPowerId,
        report_number: row.reportNumber || undefined,
        fitness_status: row.fitnessStatus || undefined,
        remarks: row.remarks || undefined,
      })),
    });

    const reportMap = Object.fromEntries(
      (response.reports || []).map((report) => [
        String(report.actual_man_power_id),
        report.report_id,
      ]),
    );

    const uploads = validRows
      .filter((row) => row.files.length && reportMap[row.actualManPowerId])
      .map((row) =>
        hrRecordsApi.uploadMedicalReportFile(
          reportMap[row.actualManPowerId],
          row.files,
        ),
      );

    if (uploads.length) {
      await Promise.all(uploads);
    }

    const firstReportId = response.reports?.[0]?.report_id || response.medical_id;
    showSnackbar("Medical report created successfully");
    window.setTimeout(() => {
      navigate(firstReportId ? `/hr-medical-report-view/${firstReportId}` : "/hr-medical-report-list");
    }, 900);
  };

  const saveEdit = async () => {
    const row = employeeRows[0];

    await hrRecordsApi.updateMedicalReport(id, {
      ...basePayload,
      report_number: row.reportNumber || undefined,
      fitness_status: row.fitnessStatus || undefined,
      remarks: row.remarks || undefined,
    });

    if (row.files.length) {
      await hrRecordsApi.uploadMedicalReportFile(id, row.files);
    }

    showSnackbar("Medical report updated successfully");
    window.setTimeout(() => {
      navigate(`/hr-medical-report-view/${id}`);
    }, 900);
  };

  const handleSave = async () => {
    if (!validate()) return;
    try {
      setLoading(true);
      if (isEdit) {
        await saveEdit();
      } else {
        await saveCreate();
      }
    } catch (error) {
      console.error("Failed to save medical report:", error);
      showSnackbar(
        getApiErrorMessage(error, "Failed to save medical report"),
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const removeExistingAttachment = async (fileId) => {
    try {
      await hrRecordsApi.deleteMedicalAttachment(fileId);
      setExistingAttachments((prev) =>
        prev.filter((file) => (file.file_id || file.id) !== fileId),
      );
      showSnackbar("Attachment deleted successfully");
    } catch (error) {
      console.error("Failed to delete attachment:", error);
      showSnackbar(
        getApiErrorMessage(error, "Failed to delete attachment"),
        "error",
      );
    }
  };

  const handleFileAction = async (request, filename, action = "download") => {
    try {
      const response = await request;
      if (action === "view") {
        openBlobResponse(response);
      } else {
        downloadBlobResponse(response, filename);
      }
    } catch (error) {
      console.error("File action failed:", error);
      showSnackbar(getApiErrorMessage(error, "File action failed"), "error");
    }
  };

  return (
    <>
      <div
        className={`data-form-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-form-content">
          <div className="data-form-header">
            <div className="data-form-header-content">
              <div className="data-form-heading-content-left-side">
                <Tooltip title="Back" arrow>
                  <IoMdArrowBack
                    className="back-company"
                    onClick={() => navigate("/hr-medical-report-list")}
                  />
                </Tooltip>
                {isEdit ? "Edit Medical Report" : "New Medical Report"}
              </div>
            </div>
          </div>
          <div className="header-separator"></div>

          <div className="data-form-body">
            {pageLoading ? (
              <div className="loader-container">
                <div className="loader"></div>
                <div>Loading...</div>
              </div>
            ) : (
              <form className="form-detail">
                <div className="new-data-form hr-records-form-grid-two">
                  <div className="form-group-select">
                    <label className="hr_training_labels">
                      Medical Type <span className="text-danger">*</span>
                    </label>
                    <div className="input-group hr-records-form-control">
                      <Select
                        className="hr-records-select"
                        options={medicalTypes}
                        value={medicalTypes.find(
                          (type) => type.value === formData.medicalType,
                        )}
                        onChange={(option) =>
                          setFormData((prev) => ({
                            ...prev,
                            medicalType: option?.value || "",
                          }))
                        }
                        isClearable
                        placeholder="Select medical type"
                      />
                    </div>
                    {errors.medicalType && (
                      <div className="error-message">{errors.medicalType}</div>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="hr_training_labels">
                      Examination Date <span className="text-danger">*</span>
                    </label>
                    <div className="input-group hr-records-form-control">
                      <DatePicker
                        selected={formData.examinationDate}
                        onChange={(date) =>
                          setFormData((prev) => ({ ...prev, examinationDate: date }))
                        }
                        dateFormat="dd/MM/yyyy"
                        className="datesss"
                        wrapperClassName="hr-records-date-wrapper"
                        placeholderText="Select examination date"
                        showYearDropdown
                        scrollableYearDropdown
                        yearDropdownItemNumber={30}
                      />
                    </div>
                    {errors.examinationDate && (
                      <div className="error-message">{errors.examinationDate}</div>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="hr_training_labels">Valid From</label>
                    <div className="input-group hr-records-form-control">
                      <DatePicker
                        selected={formData.validFrom}
                        onChange={(date) =>
                          setFormData((prev) => ({ ...prev, validFrom: date }))
                        }
                        dateFormat="dd/MM/yyyy"
                        className="datesss"
                        wrapperClassName="hr-records-date-wrapper"
                        placeholderText="Select valid from date"
                        showYearDropdown
                        scrollableYearDropdown
                        yearDropdownItemNumber={30}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="hr_training_labels">Valid To</label>
                    <div className="input-group hr-records-form-control">
                      <DatePicker
                        selected={formData.validTo}
                        onChange={(date) =>
                          setFormData((prev) => ({ ...prev, validTo: date }))
                        }
                        dateFormat="dd/MM/yyyy"
                        className="datesss"
                        wrapperClassName="hr-records-date-wrapper"
                        placeholderText="Select valid to date"
                        showYearDropdown
                        scrollableYearDropdown
                        yearDropdownItemNumber={30}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="hr_training_labels">Medical Center</label>
                    <div className="input-group hr-records-form-control">
                      <input
                        type="text"
                        value={formData.medicalCenter}
                        onChange={(event) =>
                          setFormData((prev) => ({
                            ...prev,
                            medicalCenter: event.target.value,
                          }))
                        }
                        placeholder="Enter medical center"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="hr_training_labels">Examining Doctor</label>
                    <div className="input-group hr-records-form-control">
                      <input
                        type="text"
                        value={formData.examiningDoctor}
                        onChange={(event) =>
                          setFormData((prev) => ({
                            ...prev,
                            examiningDoctor: event.target.value,
                          }))
                        }
                        placeholder="Enter examining doctor"
                      />
                    </div>
                  </div>

                  {!isEdit && (
                    <>
                      <div className="form-group-select">
                        <label className="hr_training_labels">Shared Fitness Status</label>
                        <div className="input-group hr-records-form-control">
                          <Select
                            className="hr-records-select"
                            options={FITNESS_STATUS_OPTIONS}
                            value={FITNESS_STATUS_OPTIONS.find(
                              (status) => status.value === formData.fitnessStatus,
                            )}
                            onChange={(option) =>
                              setFormData((prev) => ({
                                ...prev,
                                fitnessStatus: option?.value || "",
                              }))
                            }
                            isClearable
                            placeholder="Select shared fitness status"
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label className="hr_training_labels">Shared Remarks</label>
                        <div className="input-group hr-records-form-control">
                          <textarea
                            rows={3}
                            value={formData.remarks}
                            onChange={(event) =>
                              setFormData((prev) => ({ ...prev, remarks: event.target.value }))
                            }
                            style={{ width: "100%", resize: "vertical" }}
                            placeholder="Enter shared remarks"
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <div className="hr-records-section">
                  <div className="hr-records-section-title">
                    {isEdit ? "Employee Record" : "Employees"}
                  </div>
                  {errors.employeeRows && (
                    <div className="error-message">{errors.employeeRows}</div>
                  )}

                  {employeeRows.map((row, index) => (
                    <div className="hr-records-participant-card" key={`${row.actualManPowerId || "new"}-${index}`}>
                      <div className="hr-records-participant-header">
                        <strong>{isEdit ? "Medical Record" : `Employee ${index + 1}`}</strong>
                        {!isEdit && (
                          <button
                            type="button"
                            className="data-form-cancel-button"
                            onClick={() => removeRow(index)}
                          >
                            Remove
                          </button>
                        )}
                      </div>

                      <div className="hr-records-card-grid hr-records-medical-employee-grid">
                        <div className="hr-records-card-field">
                          <label>Employee</label>
                          <Select
                            className="hr-records-select"
                            isDisabled={isEdit}
                            options={employees.filter(
                              (employee) =>
                                !selectedEmployeeValues.includes(String(employee.value)) ||
                                String(employee.value) === String(row.actualManPowerId),
                            )}
                            value={
                              employees.find(
                                (employee) =>
                                  String(employee.value) === String(row.actualManPowerId),
                              ) || null
                            }
                            onChange={(option) =>
                              handleRowChange(index, "actualManPowerId", option?.value || "")
                            }
                            isSearchable
                            placeholder="Select employee"
                          />
                        </div>

                        <div className="hr-records-card-field-input">
                          <label>Report Number</label>
                          <input
                            type="text"
                            value={row.reportNumber}
                            onChange={(event) =>
                              handleRowChange(index, "reportNumber", event.target.value)
                            }
                            placeholder="Enter report number"
                          />
                        </div>

                        <div className="hr-records-card-field">
                          <label>{isEdit ? "Fitness Status" : "Fitness Status Override"}</label>
                          <Select
                            className="hr-records-select"
                            options={FITNESS_STATUS_OPTIONS}
                            value={FITNESS_STATUS_OPTIONS.find(
                              (status) => status.value === row.fitnessStatus,
                            )}
                            onChange={(option) =>
                              handleRowChange(index, "fitnessStatus", option?.value || "")
                            }
                            isClearable
                            placeholder={
                              isEdit
                                ? "Select fitness status"
                                : "Select fitness status override"
                            }
                          />
                        </div>

                        <div className="hr-records-card-field-input hr-records-card-field-span-2">
                          <label>{isEdit ? "Remarks" : "Remarks Override"}</label>
                          <textarea
                            rows={2}
                            value={row.remarks}
                            onChange={(event) =>
                              handleRowChange(index, "remarks", event.target.value)
                            }
                            style={{ width: "100%", resize: "vertical" }}
                            placeholder={
                              isEdit ? "Enter remarks" : "Enter remarks override"
                            }
                          />
                        </div>
                        <div className="hr-records-card-upload">
                          <label>Attachments</label>
                          <HrUploadControl
                            buttonText="Upload File"
                            popupTitle="Report Attachments"
                            newFiles={row.files}
                            existingFiles={
                              isEdit && index === 0 ? existingAttachments : []
                            }
                            onFilesSelected={(files) =>
                              handleRowChange(index, "files", [...row.files, ...files])
                            }
                            onValidationError={(message) =>
                              showSnackbar(message, "error")
                            }
                            onRemoveNewFile={(fileIndex) =>
                              handleRowChange(
                                index,
                                "files",
                                row.files.filter((_, current) => current !== fileIndex),
                              )
                            }
                            onRemoveExistingFile={(file) =>
                              removeExistingAttachment(file.file_id || file.id)
                            }
                            onViewExistingFile={(file) =>
                              handleFileAction(
                                hrRecordsApi.viewMedicalAttachment(
                                  file.file_id || file.id,
                                ),
                                file.filename || file.name,
                                "view",
                              )
                            }
                            onDownloadExistingFile={(file) =>
                              handleFileAction(
                                hrRecordsApi.downloadMedicalAttachment(
                                  file.file_id || file.id,
                                ),
                                file.filename || file.name,
                              )
                            }
                          />
                        </div>
                      </div>
                    </div>
                  ))}

                  {!isEdit && (
                    <button
                      type="button"
                      className="hr-records-add-card-button"
                      onClick={addRow}
                    >
                      <HiOutlinePlus /> Add Employee
                    </button>
                  )}
                </div>

              </form>
            )}
          </div>

          <div className="footer">
            <div className="button-group">
              <button
                className="data-form-save-button"
                type="button"
                onClick={handleSave}
                disabled={loading || pageLoading}
              >
                {loading ? "Saving..." : "Save"}
              </button>
              <button
                className="data-form-cancel-button"
                type="button"
                onClick={() => navigate("/hr-medical-report-list")}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px", marginLeft: "20px" }}
      >
        <MuiAlert severity={snackbarSeverity}>{snackbarMessage}</MuiAlert>
      </Snackbar>
    </>
  );
}

export default MedicalReportFormPage;
